import React, { useState } from 'react';
import { Head, Link, useForm, usePage } from '@inertiajs/react';
import { PublicLayout } from '@/components/PublicLayout';

interface Newsletter {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  access_type: 'open_access' | 'restricted_access';
  subscription_type: 'open_subscription' | 'auth_required';
  delivery_schedule: 'manual' | 'daily' | 'weekly' | 'monthly';
  is_active: boolean;
}

interface Props {
  newsletters: Newsletter[];
  flash?: {
    notice?: string;
    alert?: string;
  };
}

export default function NewslettersIndex({ newsletters, flash }: Props) {
  const { auth } = usePage().props as { auth?: { user?: { id: number; email: string; name: string } } };
  const user = auth?.user;
  
  const [subscribingNewsletter, setSubscribingNewsletter] = useState<number | null>(null);
  const { data, setData, post, processing, errors, reset } = useForm({
    email: '',
    name: '',
  });

  const handleSubscribe = (newsletterId: number) => {
    setSubscribingNewsletter(newsletterId);
  };

  const handleSubmitEmail = (e: React.FormEvent, newsletterSlug: string) => {
    e.preventDefault();
    post(`/newsletters/${newsletterSlug}/subscribe_email`, {
      onSuccess: () => {
        reset();
        setSubscribingNewsletter(null);
      },
    });
  };

  const getScheduleText = (schedule: string) => {
    switch (schedule) {
      case 'daily':
        return 'Sent daily';
      case 'weekly':
        return 'Sent weekly';
      case 'monthly':
        return 'Sent monthly';
      default:
        return 'Sent manually';
    }
  };

  const publicNewsletters = newsletters.filter((n) => n.access_type === 'open_access' && n.is_active);

  return (
    <PublicLayout user={user}>
      <Head title="Newsletters" />
      <div className="container py-5">
        <div className="row">
          <div className="col-lg-8 mx-auto">
            <h1 className="mb-4">Newsletters</h1>
            
            {flash?.notice && (
              <div className="alert alert-success">{flash.notice}</div>
            )}
            {flash?.alert && (
              <div className="alert alert-danger">{flash.alert}</div>
            )}

            {publicNewsletters.length === 0 ? (
              <div className="alert alert-info">
                No newsletters available at the moment. Check back later!
              </div>
            ) : (
              <div className="row">
                {publicNewsletters.map((newsletter) => (
                  <div key={newsletter.id} className="col-md-6 mb-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <h5 className="card-title">{newsletter.name}</h5>
                        {newsletter.description && (
                          <p className="card-text text-muted">{newsletter.description}</p>
                        )}
                        <div className="mb-3">
                          <small className="text-muted">
                            {getScheduleText(newsletter.delivery_schedule)}
                          </small>
                        </div>

                        {newsletter.subscription_type === 'open_subscription' ? (
                          <>
                            {subscribingNewsletter === newsletter.id ? (
                              <form onSubmit={(e) => handleSubmitEmail(e, newsletter.slug)}>
                                <div className="mb-2">
                                  <input
                                    type="email"
                                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                    placeholder="Your email address"
                                    value={data.email}
                                    onChange={(e) => setData('email', e.target.value)}
                                    required
                                  />
                                  {errors.email && (
                                    <div className="invalid-feedback">{errors.email}</div>
                                  )}
                                </div>
                                <div className="mb-2">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Your name (optional)"
                                    value={data.name}
                                    onChange={(e) => setData('name', e.target.value)}
                                  />
                                </div>
                                <div className="d-flex gap-2">
                                  <button
                                    type="submit"
                                    className="btn btn-primary btn-sm"
                                    disabled={processing}
                                  >
                                    {processing ? 'Subscribing...' : 'Subscribe'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => {
                                      setSubscribingNewsletter(null);
                                      reset();
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            ) : (
                              <button
                                className="btn btn-primary"
                                onClick={() => handleSubscribe(newsletter.id)}
                              >
                                Subscribe
                              </button>
                            )}
                          </>
                        ) : (
                          <div className="alert alert-info mb-0 py-2">
                            <small>
                              This newsletter requires a free account. 
                              <Link href="/signin" className="alert-link ms-1">
                                Sign in to subscribe
                              </Link>
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {user && (
              <div className="mt-5">
                <h3>Your Subscriptions</h3>
                <p>
                  <Link href="/newsletters/my_subscriptions" className="btn btn-outline-primary">
                    Manage Your Newsletter Subscriptions
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
